export const JOURNEY_ACTION_TABLE_VALUE = "__journeyAction__";

export const journeyActionConditionConfig = {
  conditionSources: [
    { value: "table", label: "Table Data" },
    { value: "journeyAction", label: "Journey Actions" },
  ],
  executionStates: [
    { value: "executed", label: "Executed" },
    { value: "notExecuted", label: "Not Executed" },
  ],
  channelTypes: [
    { value: "whatsapp", label: "WhatsApp" },
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
  ],
};

export const isJourneyActionCondition = (condition) =>
  condition?.conditionSource === "journeyAction" ||
  condition?.table === JOURNEY_ACTION_TABLE_VALUE;

export const normalizeCondition = (condition) => {
  if (!condition) return condition;

  if (isJourneyActionCondition(condition)) {
    const actionRef =
      condition.actionRef ||
      (condition.value
        ? {
            templateId: Number(condition.value) || condition.value,
            templateName: condition.actionLabel || "",
          }
        : null);

    return {
      ...condition,
      conditionSource: "journeyAction",
      table: JOURNEY_ACTION_TABLE_VALUE,
      executionState: condition.executionState || condition.field || "",
      channelType: condition.channelType || condition.operator || "",
      actionRef,
      value:
        actionRef?.templateId != null ? String(actionRef.templateId) : condition.value || "",
    };
  }

  return {
    ...condition,
    conditionSource: condition.conditionSource || "table",
  };
};

export const isValidJourneyActionCondition = (condition) => {
  const normalized = normalizeCondition(condition);
  const templateId =
    normalized.actionRef?.templateId ?? normalized.value;

  return Boolean(
    normalized.executionState &&
      normalized.channelType &&
      templateId !== "" &&
      templateId != null
  );
};

export const isValidTableCondition = (condition) =>
  Boolean(
    condition?.table &&
      condition?.field &&
      condition?.operator &&
      condition?.value !== "" &&
      condition?.value != null
  );

export const isValidCondition = (condition) => {
  if (isJourneyActionCondition(condition)) {
    return isValidJourneyActionCondition(condition);
  }
  return isValidTableCondition(condition);
};

export const formatJourneyActionConditionLabel = (condition) => {
  const normalized = normalizeCondition(condition);
  const stateLabel =
    journeyActionConditionConfig.executionStates.find(
      (o) => o.value === normalized.executionState
    )?.label || normalized.executionState;
  const channelLabel =
    journeyActionConditionConfig.channelTypes.find(
      (o) => o.value === normalized.channelType
    )?.label || normalized.channelType;
  const actionName =
    normalized.actionRef?.templateName ||
    normalized.actionLabel ||
    normalized.value;

  return `Journey Action: ${stateLabel} → ${channelLabel} → ${actionName}`;
};

export const createJourneyActionConditionPatch = (patch = {}) => ({
  conditionSource: "journeyAction",
  table: JOURNEY_ACTION_TABLE_VALUE,
  field: "",
  operator: "",
  value: "",
  executionState: "",
  channelType: "",
  actionRef: null,
  ...patch,
});

export const createTableConditionPatch = (patch = {}) => ({
  conditionSource: "table",
  executionState: undefined,
  channelType: undefined,
  actionRef: undefined,
  actionLabel: undefined,
  table: "",
  field: "",
  operator: "",
  value: "",
  ...patch,
});

const getTemplateId = (condition) => {
  const normalized = normalizeCondition(condition);
  return normalized.actionRef?.templateId ?? normalized.value;
};

export const buildJourneyActionSqlFragment = (condition, brandId, paramName) => {
  const normalized = normalizeCondition(condition);
  const templateId = getTemplateId(normalized);

  if (!templateId || !normalized.channelType || !normalized.executionState) {
    return "";
  }

  const existsBody = `EXISTS (
    SELECT 1
    FROM Journey_ActionExecutions ae
    WHERE ae.BrandId = ${brandId}
      AND ae.LeadId = l.Id
      AND ae.ChannelType = '${normalized.channelType}'
      AND ae.TemplateId = ${paramName}
      AND ae.Status = 'success'
  )`;

  if (normalized.executionState === "notExecuted") {
    return existsBody.replace("EXISTS", "NOT EXISTS");
  }

  return existsBody;
};

export const collectJourneyActionParameters = (conditions, startIndex = 0) => {
  const params = [];
  let paramIndex = startIndex;

  conditions.forEach((condition) => {
    if (!isJourneyActionCondition(condition)) return;

    const templateId = getTemplateId(normalizeCondition(condition));
    if (templateId === "" || templateId == null) return;

    params.push({
      ParameterName: `@param${paramIndex}`,
      Value: templateId,
      condition,
    });
    paramIndex += 1;
  });

  return params;
};

export const mapApiActionInstances = (items = []) =>
  items.map((item) => ({
    templateId: item.TemplateId ?? item.templateId,
    templateName: item.TemplateName ?? item.templateName ?? "Unnamed Action",
    channelType: item.ChannelType ?? item.channelType,
    automationId: item.AutomationId ?? item.automationId ?? null,
    automationName: item.AutomationName ?? item.automationName ?? null,
    nodeId: item.NodeId ?? item.nodeId ?? null,
    lastExecutedAt: item.LastExecutedAt ?? item.lastExecutedAt ?? null,
    executionCount: item.ExecutionCount ?? item.executionCount ?? 0,
  }));

export const serializeConditionsForApi = (conditions = []) =>
  conditions.map((condition) => {
    const normalized = normalizeCondition(condition);
    const base = {
      Table: normalized.table,
      Field: normalized.field,
      Operator: normalized.operator,
      Value:
        normalized.value != null && normalized.value !== ""
          ? String(normalized.value)
          : normalized.value,
      LogicalOperator: normalized.logicalOperator || "AND",
    };

    if (!isJourneyActionCondition(normalized)) {
      return base;
    }

    const templateId =
      normalized.actionRef?.templateId ?? normalized.value ?? "";

    return {
      ...base,
      ConditionSource: "journeyAction",
      ExecutionState: normalized.executionState || normalized.field,
      ChannelType: normalized.channelType || normalized.operator,
      Value: String(templateId),
    };
  });

export const mapTemplateFallbackInstances = (channelType, items = []) =>
  items.map((item) => {
    if (channelType === "whatsapp") {
      return {
        templateId: item.TemplateId ?? item.Id,
        templateName: item.TemplateName ?? item.Name ?? "WhatsApp Template",
        channelType: "whatsapp",
        automationId: null,
        automationName: null,
        nodeId: null,
        lastExecutedAt: null,
        executionCount: 0,
        isTemplateFallback: true,
      };
    }

    if (channelType === "sms") {
      return {
        templateId: item.TemplateId ?? item.Id,
        templateName: item.TemplateName ?? item.Name ?? "SMS Template",
        channelType: "sms",
        automationId: null,
        automationName: null,
        nodeId: null,
        lastExecutedAt: null,
        executionCount: 0,
        isTemplateFallback: true,
      };
    }

    return {
      templateId: item.Id ?? item.id,
      templateName: item.name ?? item.Name ?? item.Email ?? "Email Template",
      channelType: "email",
      automationId: null,
      automationName: null,
      nodeId: null,
      lastExecutedAt: null,
      executionCount: 0,
      isTemplateFallback: true,
    };
  });
